import {
  QcVerificationStatus,
} from "../../constants/CommonConstants";

export interface MetaData {
  paymentType: string;
  totalAmount: string;
  transactionDate: Date;
  amountToCollect: string;
  collectedAmount: string;
  currency: string;
  category: string;
  deliveryType: string;
  modeOfCollection: string;
  invoiceNumber: string;
  internalExternal: string;
  origin: string;
  status: string;
  deliveryStatus: string;
  routeId: string;
  routeName: string;
  routeStatus: string;
  numberOfItems: number;
  lastStatusUpdatedTime: string;
  deliveryZone: string;
  activity?: string;
  assignmentDate?: string;
}


export interface Name {
  ar: string;
  en: string;
}
export interface Product {
  sku: string;
  entryNumber: string;
  hasRejectedProducts: boolean;
  name: Name;
  quantity: string;
  acceptedQty: string;
  unitPrice: string;
  imgUrl: string;
  vatPercentage: string;
  uom: string;
  orderedBarcode: string;
  associatedBarcodes: string[];
  substitution: boolean;
  amendedEntry?: boolean;
  isVirtualProduct?: boolean; 
  shippedQty?: string;
  virtualProducts?: VirtualProduct[];
}
export interface Address {
  area: string;
  landmark: string;
  building: string;
  apartment: string;
  zoneNumber: string;
  makani: string;
  streetNumber: string;
  streetName: string;
  city: string;
  postalCode: string;
  countryName: string;
  countryIsoCode: string;
  longitude: string;
  latitude: string;
  status: string;
}

export interface ContactInfo {
  name: string;
  phone: string;
  altphone: string;
  email: string;
  address: Address;
}

export interface PosInformation {
  store: string;
  storeName: string;
  contactInfo: ContactInfo;
}

export interface DeliverySlot {
  startTime: Date;
  endTime: Date;
  promiseDeliveryTime: any;
  deliverySlotTime: string;
}

export interface CustomerContactInfo {
  name: string;
  phone: string;
  email: string;
  address: Address;
}

export interface DeliveryInformation {
  deliveryInstructions: string;
  deliveryCost: string;
  carrier: string;
  carrierTrackingId: string;
  shipmentSize: string;
  totalCratesCount: number;
  deliverySlot: DeliverySlot;
  customerContactInfo: CustomerContactInfo;
  driver: Driver;
  helper: Driver;
  preferredVehicle: string;
  transitTime: string;
  actualTransitTimeInMin: string;
  deliveryDurationInMin: string;
  dynamicTransitTime: string;
  estimatedTransitTime: string;
  deliveryExecutionStatus?: string;
}

export interface PickUpContactInfo {
  companyName: string;
  name: string;
  phone: string;
  email: string;
  address: Address;
}

export interface PickupSlot {
  startTime: Date;
  endTime: Date;
}

export interface PickUpInformation {
  pickUpContactInfo: PickUpContactInfo;
  pickupSlot: PickupSlot;
}

export interface Reason {
  deliveryStatus: string;
  reasonCode: string;
  reasonDescription: string;
}

export interface SupportDriver {
  location: {
    latitude: string;
    longitude: string;
  };
  driverCheckInTime: string;
  driverId: string;
  driverName: string;
  latitude: number;
  longitude: number;
  phone: string;
  vehicleType: string;
  vendor: string;
  virtualConsignmentCode: string;
}
export interface Order {
  id: string;
  orderCode: string;
  consignmentCode: string;
  metadata: MetaData;
  posInformation: PosInformation;
  deliveryInformation: DeliveryInformation;
  reason: Reason;
  products: Product[];
  createdAt: string;
  orderEtaStatus: string;
  containsSupportDrivers: boolean;
  supportDrivers: SupportDriver[];
  assignedConsignments?: string[];
  switchingAllowed?: boolean;
  switchingEnabled?: boolean;
  rtdBreached?: boolean;
  hasClubbingSuggestion?: boolean;
}

export interface CountryDetails {
  countryCode: string;
  countryName: string;
}

export interface AssignedStatus {
  vehicleAssigned: boolean;
  helperAssigned: boolean;
  driverAssigned: boolean;
  consignmentsAvailable: boolean;
}

export interface Vehicle {
  brand: string;
  country: string;
  crateCapacity: number;
  fleetId: string;
  id: string;
  orderCapacity: number;
  type: string;
  name: string;
}

export interface Driver {
  countryIsoCode: string;
  countryName: string;
  licenseNumber: number;
  driverId: string;
  driverName: string;
  type: string;
  id: string;
  name: string;
}

export interface Sort {
  sorted: boolean;
  unsorted: boolean;
  empty: boolean;
}

export interface Pageable {
  sort: Sort;
  offset: number;
  requestContinuation: string;
  pageNumber: number;
  pageSize: number;
  paged: boolean;
  unpaged: boolean;
}

export interface ExpressOrdersDataResponse {
  pageNumber: number;
  totalElements: number;
  totalPages: number;
  continuationToken?: string;
  elements: Order[];
}

export interface Statuses {
  statusCode: string;
  statusName: string;
  statusDate: string;
  reason: Reason;
}

export interface QcStatus {
  reportedQty: string; // if this can also be a number, you could use 'string | number' instead
  verifiedQty: string;
  verified: QcVerificationStatus;
}

export interface AssociatedCrates {
  crateId: string;
  scanned: boolean;
  quantity: number;
  locationCode: string;
  qcStatus: QcStatus;
}

export enum ServiceType {
  FRESH_CUT = 'FRESH_CUT',
  EXTENDED_WARRANTY = 'EXTENDED_WARRANTY',
  EXT_WARRANTY_WITH_AD = 'EXT_WARRANTY_WITH_AD',
  ACCIDENTAL_DAMAGE = 'ACCIDENTAL_DAMAGE',
}

export interface VirtualProduct
  extends Omit<VirtualProductFieldsFromProduct, 'uom' | 'isVirtualProduct'> {
  uom: Product['uom'] | string;
  isVirtualProduct: boolean;
  parentProductCode: string;
  parentEntryNumber: string;
  serviceType: ServiceType;
}


export interface SubsProducts {
  sku: string;
  name: Name;
  quantity: string;
  shippedQty: string;
  rejectedQty: string;
  unitPrice: string;
  vatPercentage: string;
  uom: string;
  reason: Reason;
  associatedBarcodes?: string[];
  associatedCrates?: AssociatedCrates[];
  effectiveUnitPrice?: string | number;
}

export interface ProductsDataTransform {
  sku: string;
  name: Name;
  entryNumber: string;
  quantity: string;
  shippedQty: any;
  rejectedQty: string;
  unitPrice: string;
  vatPercentage: string;
  uom: string;
  reason: Reason;
  substitution: boolean;
  substitutedProducts: SubsProducts[];
  virtualProducts?: VirtualProduct[];
}

export interface StandardProducts {
  products: ProductsDataTransform[];
}

export interface ActualTransitDistance {
  totalDistanceMeters: number;
  formattedDistance: string;
}

export interface Route {
  id: string;
  routeId: string;
  creationTime: string;
  plannedDate: string;
  createdBy: string;
  carrier: string;
  deliveryProposition: string;
  deliveryArea: string[];
  deliverySlot: string[];
  countryDetails: CountryDetails;
  totalConsignment: number;
  totalCrates: number;
  transitTime: number;
  displayRouteStatus: string;
  hubCode: string;
  hubName: string;
  status: string;
  assignedStatus: AssignedStatus;
  assignedVehicle: Vehicle;
  assignee: Driver;
  helper: Driver;
  actualTransitTime: string;
  parentRouteId: string;
  routeType?: string;
  actualTransitDistance?: ActualTransitDistance;
}

export interface PlanningRoutesDataResponse {
  pageNumber: number;
  totalElements: number;
  totalPages: number;
  continuationToken?: string;
  elements: Route[];
}

export interface PaymentDetail {
  payBillNo: string;
  paymentPhoneNumber: string;
  uniqueReferenceNumber: string;
  transactionInitiatedTries: any;
  transactionStatus: any;
  timestamp: any;
  collectedAmount: string;
}

type VirtualProductFieldsFromProduct = Pick<
  Product,
  | 'sku'
  | 'entryNumber'
  | 'hasRejectedProducts'
  | 'name'
  | 'quantity'
  | 'shippedQty'
  | 'unitPrice'
  | 'imgUrl'
  | 'uom'
  | 'orderedBarcode'
  | 'associatedBarcodes'
  | 'isVirtualProduct'
  | 'substitution'
  | 'amendedEntry'
>;






